import { useQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash'
import { useParams } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { DASHBOARD_TAB, Dashboard } from '../../models/Dashboard'
import { Edition } from '../../models/Edition'
import { RecordFactory } from '../../models/RecordFactory'
import { useLoadUser } from '../../queries/useUserQueries'
import { filterDashboardItems } from '../../utils/dashboard'
import { Tab } from './DashboardPage'

function useDashboardPending(userId: string | undefined, subaccountUserId: string | undefined) {
    const { logAndDisplayError } = useErrorHandler()
    return useQuery({
        queryKey: ['dashboard', userId, subaccountUserId, 'pending'],
        queryFn: async () => {
            const result = await RecordFactory.getInstance().fetchDashboard(
                'pending',
                userId,
                subaccountUserId,
                0,
                'all'
            )
            if (!result.ok) {
                logAndDisplayError(result.error)
                return null
            }
            return result.value
        },
        onError: logAndDisplayError,
    })
}

function useDashboardComplete(userId: string | undefined, subaccountId: string | undefined) {
    const { logAndDisplayError } = useErrorHandler()
    return useQuery({
        queryKey: ['dashboard', userId, subaccountId, 'complete'],
        queryFn: async () => {
            const result = await RecordFactory.getInstance().fetchDashboard(
                'complete',
                userId,
                subaccountId,
                0,
                'all'
            )
            if (!result.ok) {
                logAndDisplayError(result.error)
                return null
            }
            return result.value
        },
    })
}

function useDashboardMainAndAccount(userId: string | undefined, subaccountId: string | undefined) {
    const { logAndDisplayError } = useErrorHandler()
    return useQuery({
        queryKey: ['dashboard', userId, subaccountId, 'main_account'],
        queryFn: async () => {
            const [account, main] = await Promise.all([
                RecordFactory.getInstance().fetchDashboards(
                    userId,
                    undefined,
                    subaccountId,
                    DASHBOARD_TAB.DASHBOARD_TAB_ACCOUNT
                ),
                RecordFactory.getInstance().fetchDashboards(
                    userId,
                    undefined,
                    subaccountId,
                    DASHBOARD_TAB.DASHBOARD_TAB_MAIN
                ),
            ])

            if (!account.ok) {
                logAndDisplayError(account.error)
                return null
            }
            if (!main.ok) {
                logAndDisplayError(main.error)
                return null
            }

            const dashboards = uniqBy(
                [...main.value.dashboards, ...account.value.dashboards],
                'id'
            ) as Dashboard[]

            const onboardingDashboards = filterDashboardItems(dashboards, [
                'setup_onboarding',
                'setup_launch',
            ])

            return {
                dashboards,
                tabs: main.value.tabs as Tab[],
                edition: main.value.edition as Edition,
                actionButtons: filterDashboardItems(dashboards, ['charge', 'invoice', 'schedule']),
                onboardingDashboard: onboardingDashboards.length
                    ? onboardingDashboards[0]
                    : undefined,
            }
        },
    })
}

function useDashboardSubaccount(subaccountId: string | undefined) {
    return useQuery({
        queryKey: ['dashboard', subaccountId],
        queryFn: async () => {
            if (!subaccountId) {
                return null
            }
            return await RecordFactory.getInstance().loadSubaccount(subaccountId)
        },
    })
}

export function useDashboards() {
    const { userId, subaccountId } = useParams()

    const {
        data: mainAndAccount,
        isLoading: isLoadingMainAndAccount,
        refetch: refetchMainAndAccount,
        isFetching: isFetchingMainAndAccount,
    } = useDashboardMainAndAccount(userId, subaccountId)
    const {
        data: unpaidBookingsDashboard,
        isLoading: isLoadingComplete,
        refetch: refetchComplete,
        isFetching: isFetchingComplete,
    } = useDashboardComplete(userId, subaccountId)
    const {
        data: bookingsDashboard,
        isLoading: isLoadingPending,
        refetch: refetchPending,
        isFetching: isFetchingPending,
    } = useDashboardPending(userId, subaccountId)

    const {
        data: client,
        isLoading: isLoadingClient,
        isFetching: isFetchingClient,
    } = useLoadUser(userId)

    const {
        data: subaccount,
        isLoading: isLoadingSubaccount,
        isFetching: isFetchingSubaccount,
    } = useDashboardSubaccount(subaccountId)

    const refetch = async () => {
        await Promise.all([refetchMainAndAccount(), refetchComplete(), refetchPending()])
    }

    const isLoading =
        isLoadingMainAndAccount ||
        isLoadingComplete ||
        isLoadingPending ||
        isLoadingClient ||
        isLoadingSubaccount

    const isError =
        !isLoading && (!mainAndAccount || !unpaidBookingsDashboard || !bookingsDashboard)

    const isFetching =
        isFetchingMainAndAccount ||
        isFetchingComplete ||
        isFetchingPending ||
        isFetchingClient ||
        isFetchingSubaccount

    return {
        data: {
            ...mainAndAccount,
            unpaidBookingsDashboard,
            bookingsDashboard,
            client: client ? client : undefined,
            subaccount: subaccount ? subaccount : undefined,
        },
        isLoading,
        isError,
        isFetching,
        refetch,
    }
}
